<script setup lang="ts">
import type { CustomPricingContext } from "@classes-service-types/index.ts";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Divider from "primevue/divider";
import Dropdown from "primevue/dropdown";
import OverlayPanel from "primevue/overlaypanel";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, onUnmounted, ref, watch } from "vue";
import { filtersPresets } from "@/components/OperationsApp/components/Filters/styles.ts";
import { hasDisplayCustomPricingBulkActionsEnabled, isTenantManager } from "@/composables/authorization";
import { useGetCustomPricingList } from "@/providers/customPricing/useGetCustomPricingList";
import BulkUpdateModal from "../BulkUpdateModal/index.vue";
import { useFilters } from "../composables/useFilters";
import { useRowSelection } from "../composables/useRowSelection";
import { useSortFilters } from "../composables/useSortFilters";
import {
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_SHOW_AT_LOAD,
  DEFAULT_ROWS_PER_PAGE,
  MAX_BULK_UPDATE_ROWS,
  ROWS_PER_PAGE_DROPDOWN_OPTIONS,
  ROWS_PER_PAGE_OPTIONS,
} from "../constants";
import { customPricingTablePresets } from "../styles";
import { formatDate, formattedPriceFromCents } from "../utils";
import { useShowColumns } from "./useShowColumns";

defineOptions({ name: "CustomPricingTable" });

const emit = defineEmits<(e: "edit", overrideId: string) => void>();

const pageLimit = ref<number>(DEFAULT_ROWS_PER_PAGE);
const pageSkip = ref<number>(0);
const currentPage = ref<number>(1);
const currentFirst = ref<number>(0);
const columnSelectorPanel = ref();
const showBulkUpdateModal = ref<boolean>(false);

const toast = useToast();

const {
  q,
  sessionId,
  homeInstitutionId,
  statusId,
  channelId,
  cityId,
  overrideId,
  titleQ,
  sessionNameQ,
  homeInstitutionNameQ,
  updatedByQ,
} = useFilters();

const { field, order } = useSortFilters();

const { selectedRows, selectedCount, hasSelection, isOverCap, canBulkUpdate, clearSelection, setRenderedRows } =
  useRowSelection();

const { displayableColumns, canDisplayColumn, columnOptions } = useShowColumns(
  COLUMN_OPTIONS,
  COLUMNS_NOT_TO_SHOW_AT_LOAD,
);

const {
  isLoading,
  data: customPricingResponse,
  isError,
} = useGetCustomPricingList(
  pageLimit,
  pageSkip,
  field,
  order,
  q,
  sessionId,
  homeInstitutionId,
  statusId,
  undefined,
  channelId,
  cityId,
  undefined,
  undefined,
  undefined,
  overrideId,
  titleQ,
  sessionNameQ,
  homeInstitutionNameQ,
  updatedByQ,
  {
    refetchInterval: false,
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      toast.add({
        severity: "error",
        summary: "Custom Pricing",
        detail: `Failed to load records. ${error.message}`,
        group: "br-cp",
        life: 5000,
      });
      return false;
    },
  },
);

const hasEditPermissions = computed(() => isTenantManager());

const canSelectRows = computed<boolean>(
  () => Boolean(hasDisplayCustomPricingBulkActionsEnabled.value) && isTenantManager(),
);

const pageCount = computed<number>(() => Math.ceil((customPricingResponse.value?.data?.count || 0) / pageLimit.value));

const pageDropDownOptions = computed(() =>
  Array.from({ length: pageCount.value }, (_, i) => ({ key: `${i + 1}`, value: i + 1 })),
);

const openBulkUpdate = (): void => {
  showBulkUpdateModal.value = true;
};

const toggleColumnSelectorPanel = (event: Event): void => {
  columnSelectorPanel.value?.toggle(event);
};

const dropdownPageChange = (value: number): void => {
  const newPageSkip = (value - 1) * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }
  currentPage.value = value;
  currentFirst.value = newPageSkip;
};

const dataTablePageChange = (event: { page: number; rows: number }): void => {
  const newPageSkip = event.page * pageLimit.value;
  if (pageSkip.value !== newPageSkip) {
    pageSkip.value = newPageSkip;
  }
  if (event.rows !== pageLimit.value) {
    pageLimit.value = event.rows;
  }
  currentPage.value = event.page + 1;
  currentFirst.value = newPageSkip;
};

watch(
  () => customPricingResponse.value?.data?.items,
  (items) => {
    setRenderedRows((items ?? []) as CustomPricingContext[]);
  },
  { immediate: true },
);

// Filtering from page 5 otherwise keeps skip at 200 and lands the operator on an empty table.
watch(
  [
    q,
    sessionId,
    homeInstitutionId,
    statusId,
    channelId,
    cityId,
    overrideId,
    titleQ,
    sessionNameQ,
    homeInstitutionNameQ,
    updatedByQ,
    pageLimit,
  ],
  () => {
    pageSkip.value = 0;
    currentPage.value = 1;
    currentFirst.value = 0;
  },
);

// Selection outlives filter and page changes by design, so leaving the screen is what has to end it.
onUnmounted(clearSelection);
</script>

<template>
  <div class="mt-6">
    <Toast position="bottom-right" group="br-cp" />
    <BulkUpdateModal v-model:visible="showBulkUpdateModal" />
    <!-- Wraps rather than fixing a height: the selection group pushes the row past a narrow
         viewport, and both halves have to stay reachable. The floor keeps the row from shifting
         as that group appears and disappears. -->
    <div class="flex flex-wrap justify-between items-center gap-y-2 min-h-8 my-4">
      <div class="flex items-center gap-3">
        <Button
          label="BULK EDIT"
          data-test-id="bulk-update-open"
          v-show="canSelectRows"
          :disabled="!canBulkUpdate"
          link
          @click="openBulkUpdate"
          :pt="customPricingTablePresets.bulkEditButton"
        />
        <template v-if="hasSelection">
          <span
            class="text-sm font-semibold"
            :class="isOverCap ? 'text-red-700' : 'text-teal-900'"
            data-test-id="bulk-update-selection-count"
          >
            {{ selectedCount }} selected<template v-if="isOverCap"> — maximum {{ MAX_BULK_UPDATE_ROWS }}</template>
          </span>
          <button class="text-sm text-teal-900 underline" @click="clearSelection">Clear selection</button>
        </template>
      </div>
      <div class="align-content-end flex items-center ml-auto">
        <div class="mr-2">{{ customPricingResponse?.data?.count }} Items</div>
        <Dropdown
          :model-value="currentPage"
          @update:modelValue="dropdownPageChange"
          option-label="key"
          option-value="value"
          :options="pageDropDownOptions"
        />
        <div class="ml-2">of {{ Number.parseInt(`${pageCount}`, 10) }}</div>
        <Divider class="!m-3" layout="vertical" />
        <div>View</div>
        <Dropdown
          class="ml-2"
          v-model="pageLimit"
          option-label="key"
          option-value="value"
          :options="ROWS_PER_PAGE_DROPDOWN_OPTIONS"
        />
        <Button link @click="toggleColumnSelectorPanel">
          <span class="material-symbols-outlined">settings</span>
        </Button>
        <OverlayPanel ref="columnSelectorPanel">
          <div class="flex flex-col">
            <div
              v-for="columnOption of columnOptions"
              :key="columnOption.key"
              class="flex items-center mb-4"
            >
              <Checkbox
                v-model="displayableColumns"
                :inputId="columnOption.key"
                :name="columnOption.key"
                :value="columnOption.key"
                :pt="filtersPresets.checkbox"
              />
              <label :for="columnOption.key" class="ml-4">{{ columnOption.name }}</label>
            </div>
          </div>
        </OverlayPanel>
      </div>
    </div>

    <DataTable
      data-test-id="custom-pricing-table"
      :value="customPricingResponse?.data?.items || []"
      :pt="customPricingTablePresets.datatable"
      paginator
      :always-show-paginator="true"
      :rows="pageLimit"
      :totalRecords="customPricingResponse?.data?.count || 0"
      :rowsPerPageOptions="ROWS_PER_PAGE_OPTIONS"
      :loading="isLoading"
      v-model:selection="selectedRows"
      dataKey="id"
      resizableColumns
      columnResizeMode="fit"
      tableStyle="min-width: 50rem"
      table-class="w-50 min-h-40"
      :lazy="true"
      :first="currentFirst"
      @page="dataTablePageChange"
    >
      <Column
        selectionMode="multiple"
        column-key="select_box"
        headerStyle="width: 3rem"
        :hidden="!canSelectRows"
      />

      <Column v-if="canDisplayColumn('id')" header="ID" column-key="id" class="w-[220px]">
        <template #body="slotProps">
          <div class="break-all text-gray-400">{{ slotProps.data.id }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('home_institution_name')" header="Institution" column-key="home_institution_name" class="w-[200px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.home_institution_name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('home_institution_id')" header="Institution ID" column-key="home_institution_id" class="w-[180px]">
        <template #body="slotProps">
          <div class="break-all text-gray-400">{{ slotProps.data.home_institution_id }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('session_name')" header="Session Name" column-key="session_name" class="w-[240px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.session_name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('session_id')" header="Session ID" column-key="session_id" class="w-[220px]">
        <template #body="slotProps">
          <div class="break-all text-gray-400">{{ slotProps.data.session_id }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('title')" header="Scholarship Title" column-key="title" class="w-[200px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.title }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('class_fees_price')" header="Class Price" column-key="class_fees_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.class_academic_fees_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_class_fees_price')" header="Original Class Price" column-key="base_class_fees_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.class_academic_fees_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('internships_price')" header="Internship Price" column-key="internships_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.internships_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_internships_price')" header="Original Internship Price" column-key="base_internships_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.internships_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_price')" header="Total Price" column-key="base_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.base_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_housing_price')" header="Original Housing Price" column-key="base_housing_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.housing_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_insurance_price')" header="Original Insurance Price" column-key="base_insurance_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.insurance_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_group_flight_price')" header="Original Group Flight Price" column-key="base_group_flight_price" class="w-[170px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.group_flight_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_public_transportation_price')" header="Original Public Transportation Price" column-key="base_public_transportation_price" class="w-[200px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.public_transportation_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_events_price')" header="Original Events Price" column-key="base_events_price" class="w-[150px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.events_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('base_total_academic_fees_price')" header="Original Total Academic Fees Price" column-key="base_total_academic_fees_price" class="w-[200px]">
        <template #body="slotProps">
          <div class="break-words">{{ formattedPriceFromCents(slotProps.data.session_base_prices?.total_academic_fees_price_in_cents) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('academic_year')" header="Session Academic Year" column-key="academic_year" class="w-[130px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.academic_year }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('status')" header="Session Status" column-key="status" class="w-[120px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.status?.name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('channel')" header="Session Channel" column-key="channel" class="w-[120px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.channel?.name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('owner')" header="Session Owner" column-key="owner" class="w-[160px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.owner?.name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('city')" header="Session City" column-key="city" class="w-[120px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.city?.name }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('marketplace')" header="Session Marketplace" column-key="marketplace" class="w-[120px]">
        <template #body="slotProps">
          <div>{{ slotProps.data.marketplace ? 'Yes' : 'No' }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('created_at')" header="Created" column-key="created_at" class="w-[110px]">
        <template #body="slotProps">
          <div class="break-words">{{ formatDate(slotProps.data.created_at) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('updated_at')" header="Updated" column-key="updated_at" class="w-[110px]">
        <template #body="slotProps">
          <div class="break-words">{{ formatDate(slotProps.data.updated_at) }}</div>
        </template>
      </Column>

      <Column v-if="canDisplayColumn('updated_by')" header="Updated By" column-key="updated_by" class="w-[160px]">
        <template #body="slotProps">
          <div class="break-words">{{ slotProps.data.updated_by ?? "—" }}</div>
        </template>
      </Column>

      <Column header="Actions" column-key="actions" class="w-[90px]">
        <template #body="slotProps">
          <Button
            v-if="hasEditPermissions"
            :pt="customPricingTablePresets.actionButton"
            class="p-button-outlined"
            @click="emit('edit', slotProps.data.id)"
          >
            <div class="flex items-center">
              <span class="material-symbols-outlined text-sm mr-1 leading-5">edit</span>
              <div>Edit</div>
            </div>
          </Button>
        </template>
      </Column>

      <template #empty>
        No Results Found.
      </template>
    </DataTable>
  </div>
</template>

<style scoped>
</style>
